import { NEW_MASCOTS } from '@daymade/common-lib/constants';
import ImageWithFallback from 'components/ImageWithFallback';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { TSubscriptions } from 'stores/UserStore';
import {
  getRandomMascotAvatar,
  resolveUserAvatar,
} from 'utils/dashboard/avatar';
import AboutUsModule from '../../about-us/AboutUsModule';
import AboutUsSiteContainer from '../../about-us/AboutUsSiteContainer';
import Edit from '../../svg/Edit16.svg';
import { LOADING_TIMEOUT } from '../common/constants';
import EditProfileModal from './EditProfileModal';
import styles from './HeaderProfile.module.css';

const HeaderProfile = observer(({ loading }: { loading?: boolean }) => {
  const {
    currentUser,
    subscriptions,
    userInitialized,
    subscriptionsInitialized,
  } = useUserStore();
  const { isMobile } = useIsMobile();

  const [headerLoading, setHeaderLoading] = useState(true);
  const [subscription, setSubscription] = useState<TSubscriptions[number]>();
  const [name, setName] = useState<string>();
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [currentAvatar, setCurrentAvatar] = useState(
    currentUser?.avatarLocation,
  );
  const randomAvatar = useMemo(() => getRandomMascotAvatar(), []);

  useEffect(() => {
    const activeSubscription = subscriptions.find(
      (sub) =>
        sub.subscription.status === 'ACTIVE' ||
        sub.subscription.status === 'SNOOZED' ||
        sub.subscription.status === 'FAILING',
    );

    setSubscription(activeSubscription);
  }, [subscriptions, subscriptionsInitialized]);

  useEffect(() => {
    setCurrentAvatar(currentUser?.avatarLocation);
    setName(currentUser?.firstName);
  }, [currentUser, userInitialized]);

  useEffect(() => {
    if (userInitialized && loading === undefined) {
      return setHeaderLoading(false);
    }

    if (userInitialized && !loading) {
      setTimeout(() => setHeaderLoading(false), LOADING_TIMEOUT);
    }
  }, [userInitialized, loading]);

  const openEditProfileModal = (open: boolean) => {
    setEditProfileModalOpen(open);
  };

  const avatar = resolveUserAvatar({
    avatarLocation: currentAvatar,
    randomAvatar,
  });

  const subscriptionDrawTypePlanCode =
    subscription?.drawSchedulePlans[0].drawTypePlan.code;
  const memberPrefixText =
    subscriptionDrawTypePlanCode === 'PREMIUM'
      ? 'Premium'
      : subscriptionDrawTypePlanCode === 'LITE'
        ? 'Lite'
        : subscriptionDrawTypePlanCode === 'FREEMIUM'
          ? 'Free'
          : null;

  return (
    <AboutUsModule
      background={'tealGradient'}
      withReducedPadding
      extendBackgroundBelow
    >
      <EditProfileModal
        openModal={editProfileModalOpen}
        closeModal={() => setEditProfileModalOpen(false)}
        currentAvatar={avatar}
      />
      <AboutUsSiteContainer>
        <div className={styles.headerWrapper}>
          <div className={styles.avatar}>
            {headerLoading ? (
              <Skeleton
                circle
                width={isMobile ? 67 : 98}
                height={isMobile ? 67 : 98}
              />
            ) : (
              <ImageWithFallback
                src={avatar}
                fallbackSrc={NEW_MASCOTS[1]}
                alt="User profile icon"
                layout="fill"
                objectFit="cover"
              />
            )}
          </div>
          <div className={styles.textContent}>
            <div>
              <strong className="aboutUsTypographyHeaderLarge">
                {headerLoading ? (
                  <Skeleton width={'50%'} borderRadius={12} />
                ) : (
                  <>{name ? `${name}'s Dashboard` : 'Your Dashboard'}</>
                )}
              </strong>
            </div>
            <div className={styles.links}>
              {subscription ? (
                <div
                  className={`${styles.membershipLink} aboutUsTypographyHeaderMedium`}
                >
                  {headerLoading ? (
                    <Skeleton width={200} height={28} borderRadius={12} />
                  ) : (
                    <strong>{memberPrefixText} Member</strong>
                  )}
                </div>
              ) : null}
            </div>
          </div>
          <div className={styles.editProfile}>
            <button
              className={styles.editProfileButton}
              onClick={() => openEditProfileModal(true)}
            >
              {headerLoading ? (
                <Skeleton
                  width={isMobile ? 25 : 100}
                  height={isMobile ? 25 : 24}
                  borderRadius={isMobile ? 4 : 24}
                />
              ) : (
                <>
                  <div
                    className={`aboutUsTypographyBodySmall textDecorationUnderline ${styles.editProfileText}`}
                  >
                    <>Edit profile</>
                  </div>
                  <div className={styles.editProfileIcon}>
                    <Edit />
                  </div>
                </>
              )}
            </button>
          </div>
        </div>
      </AboutUsSiteContainer>
    </AboutUsModule>
  );
});

export default HeaderProfile;
